import {
  DeferredResponse,
  DeviceHomeAlertData,
  SoftwareHomeAlertData,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns } from 'react-bulma-components';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
} from 'react-router-dom';
import { fetchDevicesHomeAlertData } from '../../devices/devices-service';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import HomeCards from '../../shared/home-cards/home-cards';
import HomeHeader from '../../shared/home-header/home-header';
import useUserPendingTasks from '../../shared/hooks/user-pending-tasks';
import { fetchSoftwareSeatsHomeAlertData } from '../../software/software-service';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SwitchToCustomerRoleModal from '../switch-to-customer-role-modal/switch-to-customer-role-modal';
import styles from './zspace-internal-home-page.module.scss';

type LoaderFunctionData = [DeviceHomeAlertData, SoftwareHomeAlertData];

export const loader: LoaderFunction = async () => {
  const devicesHomeAlertResponse = fetchDevicesHomeAlertData();
  const softwareSeatsHomeAlertResponse = fetchSoftwareSeatsHomeAlertData();

  return defer({
    response: Promise.all([
      devicesHomeAlertResponse,
      softwareSeatsHomeAlertResponse,
    ]),
  });
};

function ZspaceInternalHomePageContent() {
  const navigate = useNavigate();
  const [devicesTasksData, softwareTasksData] =
    useAsyncValue() as LoaderFunctionData;
  const userTasks = useUserPendingTasks(devicesTasksData, softwareTasksData);
  const [showSwitchRoleModal, setShowSwitchRoleModal] =
    useState<boolean>(false);

  const navigateToMySalesOrders = useCallback(() => {
    navigate('/my-sales-orders');
  }, [navigate]);

  const navigateToMyTeam = useCallback(() => {
    navigate('/users/my-team');
  }, [navigate]);

  const navigateToMySoftware = useCallback(() => {
    navigate('/my-software');
  }, [navigate]);

  const navigateToMyDevices = useCallback(() => {
    navigate('/my-devices');
  }, [navigate]);

  const homeCards = useMemo(() => {
    return [
      {
        title: 'My Sales Orders',
        description: 'Review your sales order history and their contents.',
        pendingTasks: [],
        onClick: navigateToMySalesOrders,
      },
      {
        title: 'My Team',
        description: 'View all the users related to your orders.',
        pendingTasks: [],
        onClick: navigateToMyTeam,
      },
      {
        title: 'My Devices',
        description:
          'View and rearrange all the devices in your organization and their software assignments.',
        pendingTasks: userTasks.devicesPendingTasks,
        onClick: navigateToMyDevices,
      },
      {
        title: 'My Software',
        description:
          'View all your current software titles and to which devices they have been assigned.',
        pendingTasks: userTasks.softwarePendingTasks,
        onClick: navigateToMySoftware,
      },
    ];
  }, [
    userTasks.devicesPendingTasks,
    userTasks.softwarePendingTasks,
    navigateToMySalesOrders,
    navigateToMyTeam,
    navigateToMyDevices,
    navigateToMySoftware,
  ]);

  const navigateToSelectTemporaryPermissionsPage = useCallback(() => {
    navigate('customer-management');
  }, [navigate]);

  return (
    <BoxLayout
      className={`is-min-height-80 mx-10 my-4 ${styles.layout}`}
      childrenClassName={styles.container}
    >
      <Columns
        display="flex"
        flexDirection="column"
        className="is-min-height-80"
      >
        <Columns.Column display="flex" flexDirection="column" p={4}>
          <HomeHeader />
        </Columns.Column>
        <Columns.Column>
          <HomeCards cards={homeCards} />
        </Columns.Column>
      </Columns>
      <SwitchToCustomerRoleModal
        show={showSwitchRoleModal}
        onClose={() => setShowSwitchRoleModal(false)}
        onSwitchRole={navigateToSelectTemporaryPermissionsPage}
      />
    </BoxLayout>
  );
}

export function ZspaceInternalHomePage() {
  const { response } = useLoaderData() as DeferredResponse<LoaderFunctionData>;

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <ZspaceInternalHomePageContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default ZspaceInternalHomePage;
